import { CircularProgress, Skeleton } from '@mui/material'
import PDFViewer from 'components/organisms/PDFViewer/PDFViewer'
import useDTMS3Image from 'lib/utils/useDTMS3Image'
import { InventorySheetFile } from '../InventorySheets.types'

interface FilePreviewProps {
  file: InventorySheetFile
  onDelete: (id: string) => void
}

const FilePreview = ({ file, onDelete }: FilePreviewProps) => {
  // Only use the useDTMS3Image hook for files that have been uploaded to S3
  // and are of image type (not PDFs, Word docs, etc.)
  const shouldUseS3Hook =
    file.isUploaded &&
    (file.mimeType === 'image/jpeg' ||
      file.mimeType === 'image/png' ||
      file.mimeType === 'image/gif')

  const { imageUrl, isLoading } = useDTMS3Image({
    url: shouldUseS3Hook ? file.src : undefined,
    defaultImage: '',
  })

  const renderPreview = () => {
    // If the file is currently uploading, show a loading spinner
    if (file.isUploading) {
      return (
        <div className="loading_container">
          <CircularProgress className="loader" />
        </div>
      )
    }

    // If we're waiting for the presigned URL for an image
    if (shouldUseS3Hook && isLoading) {
      return (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          className="preview-skeleton"
        />
      )
    }

    // Render based on file type
    switch (file.mimeType) {
      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
        return <img src={shouldUseS3Hook ? imageUrl : file.src} alt="preview" />
      case 'application/pdf':
        return <PDFViewer fileUrl={file.src} />
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      case 'application/vnd.ms-excel':
        return (
          <div className="file-icon excel">
            <span>Excel File</span>
          </div>
        )
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return (
          <div className="file-icon word">
            <span>Word Document</span>
          </div>
        )
      default:
        return (
          <div className="file-icon generic">
            <span>File</span>
          </div>
        )
    }
  }

  return (
    <div className="inventory-sheet_item">
      {renderPreview()}
      <button
        className="delete_button"
        onClick={() => onDelete(file.id)}
        aria-label="Delete file"
      >
        <span className="delete_icon">×</span>
      </button>
    </div>
  )
}

export default FilePreview
